<template>
  <div class="merge-token-picker" :class="{ 'merge-token-picker--dense': dense }">
    <button type="button" class="btn btn-link btn-sm merge-token-picker__toggle p-0" @click="open = !open">
      <CygnusIcon :name="open ? 'chevron-down' : 'chevron-right'" class="me-1" />{{ label }}
    </button>

    <div v-if="open" class="merge-token-picker__panel mt-2">
      <div v-if="loading" class="small text-muted">
        <CygnusIcon name="spinner" :spin="true" class="me-1" />Loading tokens…
      </div>
      <div v-else-if="error" class="small text-danger">{{ error }}</div>
      <div v-else-if="!tokens.length" class="small text-muted">No merge tokens available.</div>
      <div v-else class="merge-token-picker__groups">
        <div
          v-for="g in tokenGroups"
          :key="g.key"
          class="merge-token-picker__group"
          :class="{ 'merge-token-picker__group--enrichment': g.enrichment }"
        >
          <div class="merge-token-picker__group-label">
            {{ g.label }}
            <span v-if="g.sub" class="merge-token-picker__group-sub">{{ g.sub }}</span>
          </div>
          <div class="merge-token-picker__grid">
            <button
              v-for="t in g.tokens"
              :key="t.token"
              type="button"
              class="merge-token-picker__chip"
              :class="{
                'merge-token-picker__chip--copied': copiedToken === t.token,
                'merge-token-picker__chip--enrichment': g.enrichment,
              }"
              :title="t.label"
              @click="copyToken(t.token)"
            >
              <span class="merge-token-picker__chip-token">{{ copiedToken === t.token ? 'Copied!' : t.token }}</span>
              <span v-if="!dense && copiedToken !== t.token" class="merge-token-picker__chip-desc">{{ t.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden fallback for browsers/contexts where navigator.clipboard is unavailable
         (e.g. non-secure context). Off-screen but focusable/selectable. -->
    <input ref="fallbackInput" type="text" class="merge-token-picker__fallback-input" readonly tabindex="-1" aria-hidden="true">
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'MergeTokenPicker',
  props: {
    label: { type: String, default: 'Merge tokens' },
    dense: { type: Boolean, default: false },
  },
  emits: ['insert'],
  data() {
    return {
      api: useApi(),
      tokens: [],
      loading: false,
      error: null,
      open: false,
      copiedToken: null,
      copyTimer: null,
    };
  },
  computed: {
    // Grouped purely by token-name prefix — no API change. Company-facts
    // tokens (crawled per-recipient enrichment data) get their own group
    // with the teal accent treatment; everything else is a plain contact token.
    tokenGroups() {
      const contact = [];
      const enrichment = [];
      for (const t of this.tokens) {
        (this.isEnrichmentToken(t.token) ? enrichment : contact).push(t);
      }
      const groups = [];
      if (contact.length) {
        groups.push({ key: 'contact', label: 'Contact', sub: null, enrichment: false, tokens: contact });
      }
      if (enrichment.length) {
        groups.push({
          key: 'enrichment',
          label: 'Company intelligence',
          sub: 'Auto-filled from crawled company data per recipient',
          enrichment: true,
          tokens: enrichment,
        });
      }
      return groups;
    },
  },
  mounted() {
    this.loadTokens();
  },
  beforeUnmount() {
    clearTimeout(this.copyTimer);
  },
  methods: {
    isEnrichmentToken(token) {
      const bare = String(token).replace(/[{}]/g, '');
      return bare.startsWith('company_') || bare === 'pain_point';
    },
    async loadTokens() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/email/merge-tokens');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.tokens = Array.isArray(payload?.tokens) ? payload.tokens : [];
      } catch (e) {
        this.error = e?.message || 'Failed to load merge tokens.';
      }
      this.loading = false;
    },
    async copyToken(token) {
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(token);
          copied = true;
        }
      } catch (e) { /* fall through to fallback */ }
      if (!copied) this.copyFallback(token);

      this.$emit('insert', token);

      this.copiedToken = token;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { this.copiedToken = null; }, 1200);
    },
    copyFallback(token) {
      const input = this.$refs.fallbackInput;
      if (!input) return;
      input.value = token;
      input.focus();
      input.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore — clipboard just won't have it */ }
    },
  },
};
</script>

<style scoped>
.merge-token-picker { position: relative; }
.merge-token-picker__toggle {
  color: var(--m-text-secondary, #475569);
  font-weight: 500;
  text-decoration: none;
}
.merge-token-picker__toggle:hover { color: var(--m-accent, #4F46E5); }
.merge-token-picker__panel {
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 6px);
  background: var(--m-bg-subtle, #F8FAFC);
  padding: 10px;
  max-width: 480px;
}
.merge-token-picker__groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.merge-token-picker__group-label {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: 7px;
  row-gap: 1px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--m-text-secondary, #475569);
  margin-bottom: 6px;
}
.merge-token-picker__group-sub {
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: var(--m-accent-2, #0EA5A4);
}
.merge-token-picker__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.merge-token-picker__chip {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  border: 1px solid var(--m-border, #C7D2FE);
  background: var(--m-bg-card, #FFF);
  border-radius: var(--m-radius, 6px);
  padding: 3px 9px;
  cursor: pointer;
  text-align: left;
  line-height: 1.25;
  transition: background 0.15s, border-color 0.15s;
}
.merge-token-picker__chip:hover {
  background: var(--m-accent-soft, #EEF0FF);
  border-color: var(--m-accent, #4F46E5);
}
.merge-token-picker__chip--copied {
  background: var(--m-status-success, #10B981);
  border-color: var(--m-status-success, #10B981);
}
.merge-token-picker__chip--copied .merge-token-picker__chip-token { color: #fff; }
.merge-token-picker__chip-token {
  font-family: var(--m-font-mono, monospace);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--m-text-primary, #0F172A);
}
.merge-token-picker__chip-desc {
  font-size: 0.66rem;
  color: var(--m-text-secondary, #475569);
}
.merge-token-picker__chip--enrichment {
  border-left-width: 3px;
  border-left-color: var(--m-accent-2, #0EA5A4);
  padding-left: 7px;
}
.merge-token-picker__chip--enrichment:hover {
  border-left-color: var(--m-accent-2, #0EA5A4);
}
.merge-token-picker--dense .merge-token-picker__chip { padding: 2px 7px; }
.merge-token-picker--dense .merge-token-picker__chip--enrichment { padding-left: 5px; }
.merge-token-picker__fallback-input {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
}
</style>
